<p-drawer
  [visible]="visible()"
  (visibleChange)="onVisibleChange($event)"
  position="right"
  styleClass="pipeline-template-drawer"
  [modal]="true"
  [closeOnEscape]="true"
  libDrawerResize
  drawerResizeKey="pipeline-template"
>
  <ng-template pTemplate="header">
    <h2 class="drawer-title">
      {{ mode() === 'edit' ? 'Edit pipeline template' : 'New pipeline template' }}
    </h2>
  </ng-template>

  <ng-template pTemplate="content">
    <section class="pt-form">

      @if (mode() === 'add') {
        <p class="section-hint">
          Define the stages a candidate moves through for this kind of role.
          Templates can be applied to any job posting — recruiters won't
          have to rebuild the pipeline from scratch each time.
        </p>
      }

      <!-- ── Identity ────────────────────────────────────────────────── -->
      <div class="field field--full">
        <label class="label" for="pt-name">
          Name <span class="required">*</span>
        </label>
        <input
          id="pt-name"
          pInputText
          type="text"
          required
          aria-required="true"
          [ngModel]="name()"
          (ngModelChange)="name.set($event ?? '')"
          [class.invalid]="duplicateName()"
          placeholder="e.g. Engineering — technical track"
        />
        @if (duplicateName()) {
          <p class="hint hint--error" role="alert">
            A template with that name already exists.
          </p>
        }
      </div>

      <div class="field field--full">
        <label class="label" for="pt-description">Description</label>
        <input
          id="pt-description"
          pInputText
          type="text"
          [ngModel]="description()"
          (ngModelChange)="description.set($event ?? '')"
          placeholder="Optional — one-line summary for the template list."
        />
      </div>

      <!-- ── Role tags ──────────────────────────────────────────────── -->
      <div class="field field--full">
        <label class="label" for="pt-role-tags">Role tags</label>
        <p class="section-hint">
          Free-form tags so recruiters can filter templates by role
          type (e.g. engineering, sales, exec).
        </p>
        <div class="chip-input">
          <input
            id="pt-role-tags"
            pInputText
            type="text"
            [ngModel]="roleTagsInput()"
            (ngModelChange)="roleTagsInput.set($event ?? '')"
            (keydown)="onRoleTagKeydown($event)"
            placeholder="Type a tag then press Enter…"
          />
          <p-button
            type="button"
            label="Add"
            [text]="true"
            [disabled]="roleTagsInput().trim().length === 0"
            (click)="addRoleTag()"
          />
        </div>
        @if (roleTags().length > 0) {
          <div class="chip-list">
            @for (tag of roleTags(); track tag) {
              <p-chip
                [label]="tag"
                [removable]="true"
                (onRemove)="removeRoleTag(tag)"
              />
            }
          </div>
        }
      </div>

      <!-- ── Default flag ───────────────────────────────────────────── -->
      <div class="field field--full field--inline">
        <p-checkbox
          inputId="pt-default"
          [binary]="true"
          [ngModel]="isDefault()"
          (ngModelChange)="isDefault.set(!!$event)"
        />
        <label for="pt-default" class="inline-label">
          Use as the default template for new jobs
        </label>
      </div>

      <!-- ── Stages ─────────────────────────────────────────────────── -->
      <div class="stages-section">
        <div class="stages-header">
          <h3 class="stages-title">Stages</h3>
          <p-button
            type="button"
            label="Add stage"
            icon="pi pi-plus"
            [text]="true"
            (click)="addStage()"
          />
        </div>
        <p class="section-hint">
          Order matters — candidates move top to bottom. Mark end-state
          columns (Hired, Rejected, Withdrawn) as terminal.
        </p>

        @if (stages().length === 0) {
          <p class="empty-stages">No stages yet. Add your first stage above.</p>
        } @else {
          <!-- cdkDropList wraps the reorderable set. Each child's cdkDrag
               is anchored to a dedicated handle (cdkDragHandle) so text
               input inside the row still selects on mouse-down and doesn't
               fight the drag gesture. cdk drag-drop is keyboard-accessible
               out of the box: Space on the handle picks up, arrow keys
               move, Space / Enter drops. -->
          <ol class="stages-list" cdkDropList (cdkDropListDropped)="onStageDrop($event)">
            @for (stage of stages(); track stage.rowKey; let i = $index) {
              <li class="stage-row" cdkDrag>
                <!-- No custom *cdkDragPlaceholder — cdk's default
                     behaviour clones the row as the placeholder, so
                     the drop-target seat matches the row's actual
                     height (a tall stage with an expanded required-
                     actions textarea would otherwise poke past a
                     hardcoded placeholder height and jitter siblings
                     as the row leaves and rejoins the list). Visual
                     treatment for the clone lives on the
                     .cdk-drag-placeholder rule in the SCSS. -->

                <!-- Position badge — pinned to the top-left corner of the
                     row card so the running order reads at a glance
                     regardless of how tall the stage body grows. -->
                <span class="stage-index" aria-hidden="true">{{ i + 1 }}</span>

                <div class="stage-order">
                  <button
                    type="button"
                    class="drag-handle"
                    cdkDragHandle
                    [attr.aria-label]="'Reorder stage ' + (i + 1) + ' — space to pick up, arrows to move, space to drop'"
                    title="Drag to reorder"
                  ><i class="pi pi-bars" aria-hidden="true"></i></button>
                </div>

                <div class="stage-body">
                  <div class="stage-line stage-line--main">
                    <span
                      class="stage-swatch"
                      [style.background]="stage.color"
                      aria-hidden="true"
                    ></span>
                    <input
                      pInputText
                      type="text"
                      class="stage-name"
                      [ngModel]="stage.name"
                      (ngModelChange)="updateStageField(stage.rowKey, 'name', $event ?? '')"
                      [attr.aria-label]="'Name for stage ' + (i + 1)"
                      placeholder="Stage name (e.g. Phone screen)"
                    />
                    <!-- aria-label carries the stage name when the user has
                         typed one so a screen-reader hears exactly which
                         row will disappear on click; falls back to the
                         position number for un-named draft stages. -->
                    <button
                      type="button"
                      class="remove-stage"
                      [attr.aria-label]="stage.name.trim().length > 0
                        ? 'Remove stage ' + stage.name
                        : 'Remove stage ' + (i + 1)"
                      (click)="removeStage(stage.rowKey)"
                    ><i class="pi pi-times" aria-hidden="true"></i></button>
                  </div>

                  <div class="stage-line">
                    <!-- WAI-ARIA radiogroup pattern with roving tabindex.
                         Only the currently-selected swatch is Tab-focusable;
                         arrow keys (and Home/End) move within the group and
                         update the selection as focus moves — the standard
                         "selection follows focus" behaviour for radiogroups.
                         The keydown handler is on the container so it
                         intercepts arrow keys regardless of which child
                         holds focus at the moment. -->
                    <div
                      class="palette"
                      role="radiogroup"
                      [attr.aria-label]="paletteAriaLabel(stage)"
                      (keydown)="onPaletteKey($event, stage)"
                    >
                      <!-- focusableIdx snapshotted once per stage so
                           every swatch tests the same anchor. Also
                           handles the off-palette case: when the
                           stage's colour matches no swatch, index 0
                           becomes the tab target so the group stays
                           reachable (previously every swatch got
                           tabindex=-1 and Tab skipped the group). -->
                      @let focusableIdx = paletteFocusableIdx(stage.color);
                      @for (swatch of palette; track swatch.hex; let idx = $index) {
                        <!-- onPaletteClick updates state then focuses
                             the swatch on the next microtask (see the
                             handler for why). Sync .focus() would run
                             before CD promoted tabindex 0, leaving
                             roving-tabindex + focus briefly out of
                             sync and mis-directing a Tab pressed
                             immediately after. -->
                        <!-- aria-checked, .selected, and tabindex all
                             bind to (idx === focusableIdx) so ARIA,
                             visual, and keyboard focus all agree on
                             the same swatch — critical for sighted-
                             AT users, who otherwise saw the visual
                             show no selection while the screen reader
                             announced swatch 0 as checked (off-
                             palette state). focusableIdx returns the
                             matched-swatch index in the normal case
                             and falls back to 0 for off-palette, so
                             exactly one swatch is always visually
                             selected AND aria-checked. The group-
                             level paletteAriaLabel still announces
                             the honest off-palette state ('custom
                             #abc123') so AT users hear the true
                             stored colour even when the anchor
                             swatch is visually highlighted. -->
                        <button
                          type="button"
                          class="palette-swatch"
                          role="radio"
                          [attr.aria-checked]="idx === focusableIdx"
                          [attr.aria-label]="swatch.label"
                          [attr.tabindex]="idx === focusableIdx ? 0 : -1"
                          [class.selected]="idx === focusableIdx"
                          [style.background]="swatch.hex"
                          (click)="onPaletteClick($event, stage, swatch.hex)"
                        ></button>
                      }
                    </div>
                  </div>

                  <!-- Label as a SIBLING of the control (not a wrapping
                       parent). A <label> that both wraps a control AND
                       carries a `for` attribute has an ambiguous
                       activation target — the browser's implicit
                       containment takes precedence over `for`, so click
                       and AT associations can point to different
                       elements. Sibling <label for="..."> pointing at
                       the control's id/inputId is the unambiguous
                       ARIA-compliant pattern. Wrapper is now a plain
                       <div> for layout only. -->
                  <div class="stage-line stage-line--options">
                    <div class="stage-inline">
                      <label [attr.for]="'stage-sla-' + stage.rowKey">SLA (hours)</label>
                      <p-inputnumber
                        [ngModel]="stage.slaHours"
                        (ngModelChange)="updateStageField(stage.rowKey, 'slaHours', $event ?? null)"
                        [min]="0"
                        [showButtons]="false"
                        placeholder="Optional"
                        styleClass="sla-input"
                        [inputId]="'stage-sla-' + stage.rowKey"
                      />
                    </div>
                    <div class="stage-inline stage-inline--check">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="stage.isTerminal"
                        (ngModelChange)="updateStageField(stage.rowKey, 'isTerminal', !!$event)"
                        [inputId]="'stage-terminal-' + stage.rowKey"
                      />
                      <label [attr.for]="'stage-terminal-' + stage.rowKey">Terminal (ends the pipeline)</label>
                    </div>
                  </div>

                  <div class="stage-line">
                    <div class="stage-inline stage-inline--full">
                      <label [attr.for]="'stage-actions-' + stage.rowKey">Required actions</label>
                      <textarea
                        [id]="'stage-actions-' + stage.rowKey"
                        class="required-actions"
                        rows="2"
                        placeholder="One per line — recruiter is prompted to tick these before advancing."
                        [ngModel]="stage.requiredActions"
                        (ngModelChange)="updateStageField(stage.rowKey, 'requiredActions', $event ?? '')"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </li>
            }
          </ol>
        }
      </div>

      <!-- ── Blockers ─────────────────────────────────────────────────
           Wrapper carries role="status" + aria-live="polite" so
           validation changes are announced as the user edits. The
           wrapper (rather than the <ul> itself) hosts the live
           semantics because VoiceOver/Safari suppresses aria-live on
           elements whose ARIA role is 'list' — announcements would
           silently drop on iOS. Polite so they queue rather than
           interrupt mid-keystroke; the wrapper stays permanently in
           the DOM (no [hidden] / display:none / aria-hidden) so the
           region exists BEFORE its contents change, and CSS
           collapses .blockers when empty so no visual footprint
           remains. -->
      <div role="status" aria-live="polite">
        <ul class="hint muted blockers">
          @for (reason of blockers(); track reason) {
            <li>{{ reason }}</li>
          }
        </ul>
      </div>

      <div class="submit-row">
        <p-button
          type="button"
          label="Cancel"
          styleClass="cancel-btn"
          [text]="true"
          [disabled]="isSaving()"
          (click)="onVisibleChange(false)"
        />
        <p-button
          type="button"
          [label]="mode() === 'edit' ? 'Save changes' : 'Create template'"
          [icon]="mode() === 'edit' ? 'pi pi-check' : 'pi pi-plus'"
          styleClass="save-template-btn"
          [loading]="isSaving()"
          [disabled]="!canSave()"
          (click)="submit()"
        />
      </div>

    </section>
  </ng-template>
</p-drawer>
